import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import SearchTermReportDashboardTable from "../SearchTermReportDashboardTable";
import { idsToVisibilityState } from "../../../components/table/utils";
import { type ColumnFilter } from "@tanstack/react-table";
import { type SearchTermPerformanceV4Row } from "../api/v4SearchTermPerformanceReportByQuery";
import useSearchTermReportColumns from "../useSearchTermReportColumns";
import { VStack } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { Tabs } from "../../../components/core/basic/Tabs";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { Tab, TabList } from "react-aria-components";
import { DEFAULT_SEARCH_TERM_REPORT_SELECTED_COLUMNS, SEARCH_TERM_DIMENSIONS } from "../utils/reportingConstants";
import { type AppColumnDef, type AppRow } from "../../../components/table/tableFeatures";
import { useAcosOrRoas } from "../../../utils/acosOrRoas";

const buildExpanderInitialState = (
  columns: AppColumnDef<SearchTermPerformanceV4Row>[],
  acosOrRoas: ReturnType<typeof useAcosOrRoas>,
) => {
  const defaultHiddenCols = idsToVisibilityState(
    DEFAULT_SEARCH_TERM_REPORT_SELECTED_COLUMNS,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id as string),
  );

  return {
    columnVisibility: {
      ...defaultHiddenCols,
      acos: acosOrRoas(true, false),
      roas: acosOrRoas(false, true),
    },
    columnOrder: DEFAULT_SEARCH_TERM_REPORT_SELECTED_COLUMNS,
    columnPinning: {
      start: ["keyword_text", "query"],
      end: [],
    },
    sorting: [{ id: "impressions", desc: true }],
  };
};

export const QueryMatchExpander = ({
  row,
  categoryFilterOptions,
  parentFilters,
}: {
  row: AppRow<SearchTermPerformanceV4Row>;
  categoryFilterOptions: { value: number | string; label: string }[];
  parentFilters: ColumnFilter[];
}) => {
  const [tab, setTab] = useState("keyword");
  const { t } = useTranslation("str");
  const acosOrRoas = useAcosOrRoas();

  const keywordColumns = useSearchTermReportColumns(SEARCH_TERM_DIMENSIONS.QUERY_MATCH_KEYWORD);
  const targetColumns = useSearchTermReportColumns(SEARCH_TERM_DIMENSIONS.QUERY_MATCH_TARGET);

  const keywordInitialState = useMemo(
    () => buildExpanderInitialState(keywordColumns as AppColumnDef<SearchTermPerformanceV4Row>[], acosOrRoas),
    [keywordColumns, acosOrRoas],
  );
  const targetInitialState = useMemo(
    () => buildExpanderInitialState(targetColumns as AppColumnDef<SearchTermPerformanceV4Row>[], acosOrRoas),
    [targetColumns, acosOrRoas],
  );

  const keywordTableState = usePersistedTableState({
    persistKey: "searchTermReport-query-expander-keyword",
    initialState: keywordInitialState,
  });
  const targetTableState = usePersistedTableState({
    persistKey: "searchTermReport-query-expander-target",
    initialState: targetInitialState,
  });

  const queryFilters = useMemo<ColumnFilter[]>(
    () => [{ id: "query", value: { op: "=", value: row.original.query } }],
    [row.original.query],
  );

  const keywordParentFilters = useMemo(
    () => [...parentFilters, ...queryFilters, { id: "adv_keyword_ids_count", value: { value: 0, op: ">" } }],
    [parentFilters, queryFilters],
  );
  const targetParentFilters = useMemo(
    () => [...parentFilters, ...queryFilters, { id: "adv_target_ids_count", value: { value: 0, op: ">" } }],
    [parentFilters, queryFilters],
  );

  return (
    <VStack
      className={css({
        width: "100%",
        maxWidth: "100%",
        backgroundColor: "white",
        overflowX: "auto",
      })}
    >
      <Tabs selectedKey={tab} onSelectionChange={(nextTab) => setTab(nextTab as string)}>
        <TabList>
          <Tab id="keyword">{t(($) => $.str.tabs["Keywords"])}</Tab>
          <Tab id="targets">{t(($) => $.str.tabs["Targets"])}</Tab>
        </TabList>
      </Tabs>
      <VStack alignItems={"flex-start"} width={"100%"} paddingX={"xlarge"}>
        {tab === "keyword" ?
          <SearchTermReportDashboardTable
            state={keywordTableState.state}
            initialState={keywordTableState.initialState}
            setters={keywordTableState.setters}
            dimensions={SEARCH_TERM_DIMENSIONS.QUERY_MATCH_KEYWORD}
            categoryFilterOptions={categoryFilterOptions}
            targetType="keyword"
            parentFilters={keywordParentFilters}
            key={`${row.original.query}-keyword`}
            renderExpander={false}
            showBulkActionsButton={true}
            renderDownloadButton={false}
            columns={keywordColumns}
          />
        : <SearchTermReportDashboardTable
            state={targetTableState.state}
            initialState={targetTableState.initialState}
            setters={targetTableState.setters}
            dimensions={SEARCH_TERM_DIMENSIONS.QUERY_MATCH_TARGET}
            categoryFilterOptions={categoryFilterOptions}
            targetType="target"
            parentFilters={targetParentFilters}
            key={`${row.original.query}-target`}
            renderExpander={false}
            showBulkActionsButton={true}
            renderDownloadButton={false}
            columns={targetColumns}
          />
        }
      </VStack>
    </VStack>
  );
};
